import { Component } from "@angular/core";
import { CommonModule } from "@angular/common";
import { CanvasService } from "../../services/canvas.service";
import { CanvasProject, Slide } from "../../types/canvas.types";
import { CanvasComponent } from "../canvas/canvas.component";

@Component({
  selector: "lib-slide-navigator",
  template: `
    <div class="slide-navigator" *ngIf="project">
      <div class="slide-navigator__header">
        <h3 class="slide-navigator__title">Slides</h3>
        <button
          class="slide-navigator__add-btn canvas-btn canvas-btn-primary canvas-btn-sm"
          (click)="addSlide()"
          title="Add New Slide"
          aria-label="Add new slide"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
          </svg>
          <span class="canvas-hidden-mobile">Add Slide</span>
        </button>
      </div>

      <div class="slide-navigator__list">
        <div
          *ngFor="
            let slide of project.slides;
            let i = index;
            trackBy: trackSlide
          "
          class="slide-navigator__item"
          [class.slide-navigator__item--active]="
            i === project.currentSlideIndex
          "
          [class.slide-navigator__item--dragging]="draggedSlideIndex === i"
          [class.slide-navigator__item--drag-over]="dragOverIndex === i"
          (click)="switchSlide(i)"
          [attr.aria-label]="'Slide ' + (i + 1)"
          role="button"
          tabindex="0"
          (keydown.enter)="switchSlide(i)"
          (keydown.space)="switchSlide(i)"
          draggable="true"
          (dragstart)="onDragStart($event, i)"
          (dragover)="onDragOver($event, i)"
          (dragleave)="onDragLeave($event)"
          (drop)="onDrop($event, i)"
          (dragend)="onDragEnd($event)"
        >
          <div class="slide-navigator__preview">
            <div class="slide-navigator__number">{{ i + 1 }}</div>
            <lib-canvas
              class="slide-navigator__canvas-preview"
              [readonly]="true"
              [slideData]="slide"
            ></lib-canvas>
            <div class="slide-navigator__interaction-mask"></div>
            <div
              class="slide-navigator__empty-slide"
              *ngIf="!slide.elements || slide.elements.length === 0"
            >
              <div class="slide-navigator__empty-slide-text">Empty Slide</div>
            </div>
            <div class="slide-navigator__overlay">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path
                  d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
                />
              </svg>
            </div>
          </div>

          <!-- <div class="slide-navigator__info">
            <span class="slide-navigator__name">{{ slide.name }}</span>
            <span class="slide-navigator__count"
              >{{ slide.elements.length }} element{{
                slide.elements.length !== 1 ? "s" : ""
              }}</span
            >
          </div> -->
        </div>
      </div>

      <div
        class="slide-navigator__empty"
        *ngIf="!project.slides || project.slides.length === 0"
      >
        <div class="slide-navigator__empty-icon">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
            <path
              d="M3 3v8h8V3H3zm6 6H5V5h4v4zm-6 4v8h8v-8H3zm6 6H5v-4h4v4zm4-16v8h8V3h-8zm6 6h-4V5h4v4zm-6 4v8h8v-8h-8zm6 6h-4v-4h4v4z"
            />
          </svg>
        </div>
        <p class="slide-navigator__empty-text">No slides yet</p>
        <button
          class="canvas-btn canvas-btn-primary canvas-btn-sm"
          (click)="addSlide()"
        >
          Create your first slide
        </button>
      </div>
    </div>
  `,
  styles: [
    `
      :host {
        /* Import CSS variables */
        --canvas-primary: #232459;
        --canvas-primary-50: #e3e3ed;
        --canvas-info: #3b82f6;
        --canvas-success: #10b981;
        --canvas-accent: #706fd3;
        --canvas-gray-900: #111827;
        --canvas-gray-500: #6b7280;
        --canvas-gray-300: #d1d5db;
        --canvas-gray-400: #9ca3af;
        --canvas-bg-primary: #ffffff;
        --canvas-border-light: #e2e8f0;
        --canvas-space-1: 0.25rem;
        --canvas-space-2: 0.5rem;
        --canvas-space-3: 0.75rem;
        --canvas-space-4: 1rem;
        --canvas-space-8: 2rem;
        --canvas-text-xs: 0.75rem;
        --canvas-text-sm: 0.875rem;
        --canvas-text-base: 1rem;
        --canvas-text-lg: 1.125rem;
        --canvas-font-medium: 500;
        --canvas-font-semibold: 600;
        --canvas-font-normal: 400;
        --canvas-font-family: "Google Sans", -apple-system, BlinkMacSystemFont,
          "Segoe UI", Roboto, sans-serif;
        --canvas-radius: 0.375rem;
        --canvas-radius-lg: 0.75rem;
        --canvas-leading-tight: 1.25;
        --canvas-transition: 200ms ease;
        --canvas-transition-fast: 150ms ease;
        --canvas-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
        --canvas-shadow-sm: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
        --canvas-shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        display: block;
        width: 100%;
        height: 100%;
      }

      .slide-navigator {
        height: 100%;
        display: flex;
        flex-direction: column;
        background: var(--canvas-bg-primary);
        font-family: var(--canvas-font-family);
      }

      .slide-navigator__header {
        padding: var(--canvas-space-4);
        border-bottom: 1px solid var(--canvas-border-light);
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--canvas-space-2);
        flex-shrink: 0;
      }

      .slide-navigator__title {
        font-size: var(--canvas-text-lg);
        font-weight: var(--canvas-font-semibold);
        color: var(--canvas-gray-900);
        margin: 0;
      }

      .slide-navigator__add-btn {
        display: flex;
        align-items: center;
        gap: var(--canvas-space-2);
        white-space: nowrap;
      }

      .slide-navigator__list {
        flex: 1;
        overflow-y: auto;
        padding: var(--canvas-space-2);
        display: flex;
        flex-direction: column;
        gap: var(--canvas-space-3);
      }

      .slide-navigator__item {
        // padding: var(--canvas-space-3);
        border: 2px solid var(--canvas-border-light);
        border-radius: var(--canvas-radius-lg);
        cursor: pointer;
        transition: all var(--canvas-transition);
        background: var(--canvas-bg-primary);
        position: relative;
        outline: none;
      }

      .slide-navigator__item--dragging {
        opacity: 0.5;
        transform: rotate(2deg);
        box-shadow: var(--canvas-shadow-md);
        z-index: 1000;
      }

      .slide-navigator__item--drag-over {
        border-color: var(--canvas-primary);
        background: var(--canvas-primary-50);
        transform: translateY(-4px);
        box-shadow: 0 8px 16px rgba(35, 36, 89, 0.2);
      }

      .slide-navigator__item:hover {
        border-color: var(--canvas-primary);
        transform: translateY(-2px);
        box-shadow: var(--canvas-shadow-md);
      }

      .slide-navigator__item:focus-visible {
        border-color: var(--canvas-primary);
        box-shadow: 0 0 0 3px var(--canvas-primary-50);
      }

      .slide-navigator__item--active {
        border-color: var(--canvas-primary);
        background: var(--canvas-primary-50);
        box-shadow: var(--canvas-shadow);
      }

      .slide-navigator__item--active .slide-navigator__overlay {
        opacity: 1;
      }

      .slide-navigator__preview {
        position: relative;
        width: 100%;
        aspect-ratio: 4/3;
        background: white;
        border: 1px solid var(--canvas-border-light);
        border-radius: var(--canvas-radius);
        overflow: hidden;
        // margin-bottom: var(--canvas-space-2);
        box-shadow: var(--canvas-shadow-sm);
      }

      .slide-navigator__canvas-preview {
        width: 100%;
        height: 100%;
        pointer-events: none;
        position: relative;
      }

      .slide-navigator__interaction-mask {
        position: absolute;
        inset: 0;
        background: transparent;
        z-index: 12;
      }

      /* Override canvas component styles for the preview */
      :host ::ng-deep .slide-navigator__canvas-preview {
        display: block !important;
        width: 100% !important;
        height: 100% !important;
      }

      :host ::ng-deep .slide-navigator__canvas-preview .canvas-container {
        padding: 0 !important;
        background: transparent !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        overflow: hidden !important;
        width: 100% !important;
        height: 100% !important;
      }

      :host ::ng-deep .slide-navigator__canvas-preview .canvas-area {
        transform: scale(0.18) !important;
        transform-origin: center center !important;
        box-shadow: none !important;
        border: 1px solid var(--canvas-border-light) !important;
        border-radius: 2px !important;
        background: white !important;
        width: 800px !important;
        height: 600px !important;
        position: relative !important;
      }

      /* Ensure canvas elements are visible in preview */
      :host
        ::ng-deep
        .slide-navigator__canvas-preview
        .canvas-area
        lib-canvas-element {
        pointer-events: none !important;
      }

      .slide-navigator__number {
        position: absolute;
        top: var(--canvas-space-2);
        left: var(--canvas-space-2);
        background: var(--canvas-gray-900);
        color: white;
        font-size: var(--canvas-text-xs);
        font-weight: var(--canvas-font-semibold);
        padding: var(--canvas-space-1) var(--canvas-space-2);
        border-radius: var(--canvas-radius);
        z-index: 10;
      }

      .slide-navigator__empty-slide {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        background: #fafafa;
        border: 2px dashed var(--canvas-gray-300);
        border-radius: var(--canvas-radius);
      }

      .slide-navigator__empty-slide-text {
        font-size: var(--canvas-text-xs);
        color: var(--canvas-gray-400);
        font-weight: var(--canvas-font-medium);
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }

      .slide-navigator__overlay {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: var(--canvas-primary);
        color: white;
        border-radius: 50%;
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        opacity: 0;
        transition: all var(--canvas-transition);
        z-index: 20;
      }

      .slide-navigator__info {
        display: flex;
        flex-direction: column;
        gap: var(--canvas-space-1);
      }

      .slide-navigator__name {
        font-size: var(--canvas-text-sm);
        font-weight: var(--canvas-font-medium);
        color: var(--canvas-gray-900);
        line-height: var(--canvas-leading-tight);
      }

      .slide-navigator__count {
        font-size: var(--canvas-text-xs);
        color: var(--canvas-gray-500);
        font-weight: var(--canvas-font-normal);
      }

      .slide-navigator__empty {
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: var(--canvas-space-8);
        text-align: center;
      }

      .slide-navigator__empty-icon {
        color: var(--canvas-gray-300);
        margin-bottom: var(--canvas-space-4);
      }

      .slide-navigator__empty-text {
        font-size: var(--canvas-text-base);
        color: var(--canvas-gray-500);
        margin: 0 0 var(--canvas-space-4) 0;
        font-weight: var(--canvas-font-medium);
      }

      /* Mobile optimizations */
      @media (max-width: 640px) {
        .slide-navigator__header {
          padding: var(--canvas-space-3);
        }

        .slide-navigator__title {
          font-size: var(--canvas-text-base);
        }

        .slide-navigator__list {
          padding: var(--canvas-space-2);
          gap: var(--canvas-space-2);
        }

        .slide-navigator__item {
          padding: var(--canvas-space-2);
        }

        .slide-navigator__preview {
          margin-bottom: var(--canvas-space-1);
        }

        .slide-navigator__name {
          font-size: var(--canvas-text-xs);
        }

        .slide-navigator__count {
          font-size: 10px;
        }
      }

      /* Scrollbar styling */
      .slide-navigator__list::-webkit-scrollbar {
        width: 6px;
      }

      .slide-navigator__list::-webkit-scrollbar-track {
        background: var(--canvas-gray-100);
        border-radius: var(--canvas-radius);
      }

      .slide-navigator__list::-webkit-scrollbar-thumb {
        background: var(--canvas-gray-300);
        border-radius: var(--canvas-radius);
      }

      .slide-navigator__list::-webkit-scrollbar-thumb:hover {
        background: var(--canvas-gray-400);
      }
    `,
  ],
})
export class SlideNavigatorComponent {
  project: CanvasProject | null = null;
  draggedSlideIndex: number | null = null;
  dragOverIndex: number | null = null;

  constructor(private canvasService: CanvasService) {
    this.canvasService.project$.subscribe((project) => {
      this.project = project;
    });
  }

  addSlide(): void {
    const slideCount = this.project?.slides.length || 0;
    this.canvasService.addSlide(`Slide ${slideCount + 1}`);
  }

  switchSlide(index: number): void {
    this.canvasService.switchSlide(index);
  }

  trackSlide(index: number, slide: Slide): string {
    return slide.id;
  }

  onDragStart(event: DragEvent, slideIndex: number): void {
    this.draggedSlideIndex = slideIndex;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("text/plain", slideIndex.toString());
    }
  }

  onDragOver(event: DragEvent, slideIndex: number): void {
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = "move";
    }
    this.dragOverIndex = slideIndex;
  }

  onDragLeave(event: DragEvent): void {
    // Only clear drag over if we're leaving the entire slide item
    const target = event.target as HTMLElement;
    const currentTarget = event.currentTarget as HTMLElement;
    if (!currentTarget.contains(event.relatedTarget as Node)) {
      this.dragOverIndex = null;
    }
  }

  onDrop(event: DragEvent, toIndex: number): void {
    event.preventDefault();

    if (this.draggedSlideIndex !== null && this.draggedSlideIndex !== toIndex) {
      this.canvasService.reorderSlides(this.draggedSlideIndex, toIndex);
    }

    this.dragOverIndex = null;
  }

  onDragEnd(event: DragEvent): void {
    this.draggedSlideIndex = null;
    this.dragOverIndex = null;
  }
}
